/**
 * DemoBanner (iter-200) — sticky banner for ephemeral demo accounts.
 *
 * Shows for any authenticated user whose ``/auth/me`` returns
 * ``is_demo: true``. Two visual states:
 *   • Normal (>2h remaining) — green gradient, calm copy, dismiss
 *     button + "Create real account" CTA.
 *   • Urgent (<=2h remaining) — amber→rose gradient, live countdown,
 *     no dismiss (the user needs to see it), only "Keep my data" CTA.
 *
 * The banner ticks once per second to keep the countdown honest
 * without polling the backend.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, X, Clock, TimerReset } from "lucide-react";
import { useLang } from "../contexts/LangContext";
import { tx } from "../i18n";
import { api } from "../lib/api";

const DISMISS_KEY = "lumfi_demo_banner_dismissed";

const PERSONA_LABEL = {
  es: {
    "developer": "desarrollador/a freelance",
    "designer": "diseñador/a UX/UI",
    "content-creator": "content creator",
    "consultant": "consultor/a",
    "lawyer-accountant": "abogado/a o contador/a",
    "coach-tutor": "coach o tutor/a",
    "ecommerce": "e-commerce solo",
    "photographer": "fotógrafo/a de eventos",
  },
  en: {
    "developer": "freelance developer",
    "designer": "UX/UI designer",
    "content-creator": "content creator",
    "consultant": "consultant",
    "lawyer-accountant": "lawyer or accountant",
    "coach-tutor": "coach or tutor",
    "ecommerce": "solo e-commerce",
    "photographer": "event photographer",
  },
  pt: {
    "developer": "dev freelance",
    "designer": "designer UX/UI",
    "content-creator": "criador de conteúdo",
    "consultant": "consultor/a",
    "lawyer-accountant": "advogado/a ou contador/a",
    "coach-tutor": "coach ou tutor/a",
    "ecommerce": "e-commerce solo",
    "photographer": "fotógrafo/a de eventos",
  },
};

function formatRemaining(ms, lang) {
  if (ms <= 0) {
    return tx(lang, "expired", "expirado", "expirado");
  }
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, "0")}m`;
  return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
}

export function DemoBanner() {
  const { lang } = useLang();
  // null = unknown, false = no, {...} = active demo
  const [demo, setDemo] = useState(null);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_KEY) === "1",
  );
  const [now, setNow] = useState(() => Date.now());
  const tickRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/auth/me");
        if (cancelled) return;
        if (r.data?.is_demo) {
          setDemo({
            slug: r.data.demo_slug,
            expires_at: r.data.demo_expires_at,
            plan: r.data.demo_plan,
            packs: r.data.demo_packs || [],
            addons: r.data.demo_addons || [],
          });
        } else {
          setDemo(false);
        }
      } catch {
        if (!cancelled) setDemo(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Live countdown — 1s tick.
  useEffect(() => {
    if (!demo || !demo.expires_at) return undefined;
    tickRef.current = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [demo]);

  const remainingMs = useMemo(() => {
    if (!demo?.expires_at) return null;
    const exp = new Date(demo.expires_at).getTime();
    return exp - now;
  }, [demo, now]);

  if (!demo) return null;

  const isUrgent = remainingMs != null && remainingMs <= 2 * 3600 * 1000;
  const isExpired = remainingMs != null && remainingMs <= 0;
  // Dismiss only allowed in the non-urgent state so users can't hide
  // the warning right before their data gets wiped.
  if (dismissed && !isUrgent) return null;

  const label = (PERSONA_LABEL[lang] || PERSONA_LABEL.es)[demo.slug] || "freelance";

  if (isUrgent) {
    return (
      <div
        data-testid="demo-banner-urgent"
        className="relative z-30 bg-gradient-to-r from-amber-600 via-rose-600 to-rose-700 text-white shadow-lg"
      >
        <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center gap-3 sm:gap-4 text-sm">
          <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white/15 shrink-0 animate-pulse">
            <Clock className="h-4 w-4" />
          </span>
          <p className="flex-1 leading-snug min-w-0">
            <span className="font-bold">
              {isExpired
                ? tx(lang, "Demo expired.", "El demo expiró.", "O demo expirou.")
                : tx(lang, "Your demo ends in", "Tu demo termina en", "Seu demo termina em")}
            </span>{" "}
            {!isExpired && (
              <span
                className="font-mono font-bold bg-white/15 px-2 py-0.5 rounded text-white"
                data-testid="demo-banner-countdown"
              >
                {formatRemaining(remainingMs, lang)}
              </span>
            )}
            <span className="hidden md:inline">
              {" — "}
              {tx(lang,
                "convert now to keep your data.",
                "convierte ahora para conservar tus datos.",
                "converta agora para manter seus dados.")}
            </span>
          </p>
          <Link
            to="/convert"
            data-testid="demo-banner-convert-cta"
            className="inline-flex items-center gap-1 rounded-full bg-white text-rose-700 hover:bg-rose-50 text-xs sm:text-sm font-bold px-3.5 py-1.5 transition-colors shrink-0"
          >
            {tx(lang, "Keep my data", "Conservar mis datos", "Manter meus dados")}
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    );
  }

  // Normal (calm) state.
  return (
    <div
      data-testid="demo-banner"
      className="relative z-30 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white shadow-md"
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center gap-3 sm:gap-4 text-sm">
        <span className="hidden sm:inline-flex items-center justify-center h-7 w-7 rounded-full bg-white/15 shrink-0">
          <Sparkles className="h-4 w-4" />
        </span>
        <p className="flex-1 leading-snug min-w-0">
          <span className="font-semibold">
            {tx(lang, "You're in the live demo of a", "Estás en el demo en vivo de un/a", "Você está no demo de um/a")}
          </span>{" "}
          <span className="font-bold underline-offset-2 decoration-white/40">
            {label}
          </span>
          {remainingMs != null && (
            <span className="hidden sm:inline-flex items-center gap-1 ml-2 px-2 py-0.5 rounded bg-white/15 font-mono text-xs" data-testid="demo-banner-countdown">
              <TimerReset className="h-3 w-3" />
              {formatRemaining(remainingMs, lang)}
            </span>
          )}
          <span className="hidden md:inline">
            {" — "}
            {tx(lang,
              "explore freely, your data isn't real.",
              "explora con tranquilidad, los datos no son reales.",
              "explore à vontade, os dados não são seus.")}
          </span>
        </p>
        <Link
          to="/convert"
          data-testid="demo-banner-cta"
          className="inline-flex items-center gap-1 rounded-full bg-white text-emerald-700 hover:bg-emerald-50 text-xs sm:text-sm font-bold px-3.5 py-1.5 transition-colors shrink-0"
        >
          {tx(lang, "Create my real account", "Crear mi cuenta real", "Criar minha conta real")}
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
        <button /* design-system: ALLOW legacy-todo */
          type="button"
          onClick={() => {
            sessionStorage.setItem(DISMISS_KEY, "1");
            setDismissed(true);
          }}
          aria-label="Cerrar banner"
          data-testid="demo-banner-dismiss"
          className="h-7 w-7 rounded-full hover:bg-white/15 flex items-center justify-center transition-colors shrink-0"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
